import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

const SnackbarContext = createContext();

export const useSnackbar = () => {
  return useContext(SnackbarContext);
};

// Set by SnackbarProvider, so code outside components (hooks, API services)
// can show messages too.
let showToast = null;
let askConfirm = null;

const show = (message, severity) => {
  const text = typeof message === "string" ? message : String(message ?? "");
  if (showToast) showToast(text, severity);
  else window.alert(text); // provider not mounted yet
};

/**
 * Toast messages (top right, close by themselves):
 *   notify.success("Record Saved Successfully")
 *   notify.error(result)
 */
export const notify = {
  success: (message) => show(message, "success"),
  info: (message) => show(message, "info"),
  warning: (message) => show(message, "warning"),
  error: (message) => show(message, "error"),
};

/**
 * Asks the user to confirm. Resolves to true (confirm) or false (cancel).
 *   if (await confirmDialog("Discard your changes?")) { ... }
 */
export const confirmDialog = (
  message,
  { title = "Please confirm", confirmText = "OK", cancelText = "Cancel" } = {}
) =>
  askConfirm
    ? askConfirm({ message, title, confirmText, cancelText })
    : Promise.resolve(window.confirm(message));

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
    key: 0,
  });
  const [confirm, setConfirm] = useState(null);
  const pendingAnswer = useRef(null);

  const openSnackbar = useCallback((message, severity = "info") => {
    setSnackbar((prev) => ({ open: true, message, severity, key: prev.key + 1 }));
  }, []);

  const closeSnackbar = useCallback((event, reason) => {
    if (reason === "clickaway") return; // stay open until read or timed out
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const openConfirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        // A newer question replaces an open one; the open one counts as "cancel".
        if (pendingAnswer.current) pendingAnswer.current(false);
        pendingAnswer.current = resolve;
        setConfirm(options);
      }),
    []
  );

  const answerConfirm = (answer) => {
    const resolve = pendingAnswer.current;
    pendingAnswer.current = null;
    setConfirm(null);
    if (resolve) resolve(answer);
  };

  useEffect(() => {
    showToast = openSnackbar;
    askConfirm = openConfirm;
    return () => {
      showToast = null;
      askConfirm = null;
    };
  }, [openSnackbar, openConfirm]);

  return (
    <SnackbarContext.Provider value={{ openSnackbar, closeSnackbar }}>
      {children}
      <Snackbar
        key={snackbar.key}
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 8000 : 5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", whiteSpace: "pre-line" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={Boolean(confirm)}
        onClose={() => answerConfirm(false)}
        aria-labelledby="confirm-dialog-title"
      >
        {confirm && (
          <>
            <DialogTitle id="confirm-dialog-title">{confirm.title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{confirm.message}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => answerConfirm(false)}>
                {confirm.cancelText}
              </Button>
              <Button
                onClick={() => answerConfirm(true)}
                variant="contained"
                autoFocus
              >
                {confirm.confirmText}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </SnackbarContext.Provider>
  );
};
// ('error', 'warning', 'info', 'success'
